/** @jsx jsx */

import { Fragment } from 'react';

import {
  Alert,
  AlertDescription,
  Colors,
  Link,
  Spacer,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import PreloadPage from 'app/developer-v3/components/PreloadPage';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useCustomActions } from 'app/developer-v3/hooks';

import CustomAction from './CustomAction';

const Styles = {
  actionList: css`
    margin: 0px;
  `,
  addActionButton: css`
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  `,
  root: css`
    background: ${Colors.GrayWarm2};
    border: 1px solid ${Colors.GrayWarm4};
    border-radius: 3px;
    padding: 20px;
  `,
};

type Props = {
  selectedApi: string;
};

function CustomActions({ selectedApi }: Props) {
  const {
    customActions,
    isLoadingCustomActions,
    isMakingCustomActionPublic,
    makeCustomActionPublic,
  } = useCustomActions(selectedApi);

  const addCustomActionUrl = new URL(
    '/app/extensions/actions',
    ZAPIER_APP_BASE_URL
  );
  const searchParams = new URLSearchParams({
    selected_api: selectedApi,
    is_lifecycle_aware: 'true',
  });
  addCustomActionUrl.search = searchParams.toString();

  return (
    <section css={Styles.root}>
      <Alert title="Only Zapier staff can see this section">
        <AlertDescription>
          This is for a proof of concept experiment and only Zapier staff can
          see these Custom Actions.
        </AlertDescription>
      </Alert>
      {isLoadingCustomActions ? (
        <PreloadPage />
      ) : (
        <Fragment>
          {customActions.length === 0 ? (
            <Fragment>
              <Spacer height={30} />
              <Alert>No Custom Actions found for this integration.</Alert>
            </Fragment>
          ) : (
            <ul aria-label="Custom actions" css={Styles.actionList}>
              {customActions.map(action => (
                <CustomAction
                  accountId={action.accountId}
                  description={action.description}
                  id={action.id}
                  isMakingPublic={isMakingCustomActionPublic}
                  isPublic={action.isPublic}
                  key={action.id}
                  makePublic={makeCustomActionPublic}
                  name={action.name}
                  status={action.status}
                />
              ))}
            </ul>
          )}
          <div css={Styles.addActionButton}>
            <Link
              aria-label="Add Custom Action"
              href={addCustomActionUrl.href}
              variant="secondary"
            >
              Add Custom Action
            </Link>
          </div>
        </Fragment>
      )}
    </section>
  );
}

export default CustomActions;
